import { useEffect, useMemo, useState } from 'react';
import { BUILD_TIME_MS, FORM_PROMOS, type FormPromoPlacement, isFormPromoLive } from '../utils/formPromoConfig';
import { MAX_TIMEOUT } from '../utils/setTimeoutCeiling';

/**
 * The live GATSBY_FORM_PROMOS copy for `placement`, or null when no placement is
 * given or none of its campaigns is live.
 *
 * Until mount this judges against the build time, so the static HTML is right for
 * any build made inside a campaign's window and hydration matches it. After mount
 * it switches to the visitor's clock and sleeps until the next start or end, so a
 * campaign turns on and off at the right moment with no deploy needed.
 */
export function useFormPromoText(placement?: FormPromoPlacement): string | null {
    const promos = useMemo(
        () => (placement ? FORM_PROMOS.filter((promo) => promo.placements.includes(placement)) : []),
        [placement]
    );
    const [now, setNow] = useState(BUILD_TIME_MS);

    useEffect(() => {
        if (promos.length === 0) return;

        // A campaign is live through its end, so it comes down just after it.
        const boundaries = promos.flatMap((promo) => [promo.startMs, promo.endMs + 1]);
        let timer: ReturnType<typeof setTimeout>;

        const tick = () => {
            const current = Date.now();
            setNow(current);
            const next = Math.min(...boundaries.filter((ms): ms is number => ms !== null && ms > current));
            if (Number.isFinite(next)) timer = setTimeout(tick, Math.min(next - current, MAX_TIMEOUT));
        };

        tick();

        return () => clearTimeout(timer);
    }, [promos]);

    return promos.find((promo) => isFormPromoLive(promo, now))?.text ?? null;
}
